import Showable from '~/Controller/Showable'

/**
 * Dropdown controller for dropdown/toggle functionality
 * Extends Showable for consistent behavior
 */
export default class Dropdown extends Showable {
	// Define properties that sync with HTML attributes
	static props = {
		open: false,  // Tracks open state with data-dropdown.open
		escHide: false,  // Disable Showable's escape handling
		outClickHide: true,
		focusOnShow: false  // Disable Showable's focus handling
	}

	// Define types of elements that can connect to this controller
	static targets = ['button', 'menu']

	// Called when the controller is connected
	connected() {
		super.connected()

		this.element.setAttribute('role', 'menu')

		// Bind handlers
		this.boundHandleOtherDropdownOpen = this.handleOtherDropdownOpen.bind(this)
		this.boundHandleTab = this.boundHandleTab.bind(this)
		this.boundHandleEscape = this.handleEscape.bind(this)

		// Listen for other dropdowns opening
		this.listeners.add(document, 'dropdown:open', this.boundHandleOtherDropdownOpen)
	}

	// Called when the controller is disconnected
	disconnected() {
		super.disconnected()
	}

	// Called when a button target is connected
	buttonTargetConnected(button) {
		button.setAttribute('aria-expanded', this.open)

		this.listeners.add(button, 'click', (e) => {
			e.preventDefault()
			this.open = !this.open
		})

		// Add tab and escape handler to button
		this.listeners.add(button, 'keydown', this.boundHandleTab)
		this.listeners.add(button, 'keydown', this.boundHandleEscape)
	}

	// Called when a menu target is connected
	menuTargetConnected(menu) {
		menu.hidden = !this.open

		// Add tab and escape handler for focus trapping
		this.listeners.add(menu, 'keydown', this.boundHandleTab)
		this.listeners.add(menu, 'keydown', this.boundHandleEscape)
	}

	// Called when the 'open' prop/property changes
	openPropChanged(oldValue, newValue) {
		this.buttonTargets.forEach(btn => {
			btn.setAttribute('aria-expanded', newValue)
		})

		if (this.menuTarget) {
			this.menuTarget.hidden = !newValue
		}

		if (newValue) {
			// Notify other dropdowns
			document.dispatchEvent(new CustomEvent('dropdown:open', {
				detail: { dropdown: this.element },
				bubbles: false
			}))

			// Trigger show to activate Showable features
			this.show({ trigger: 'dropdown' })

			// Focus first focusable element
			this.focusFirstElement()
		} else {
			// Trigger hide to activate Showable features
			this.hide({ trigger: 'dropdown' })

			// Focus trigger when closing
			this.focusTrigger()
		}
	}

	// Override onShow to sync with open prop
	onShow(event) {
		if (!this.open) {
			this.open = true
			return false
		}
		return super.onShow(event)
	}

	// Override onHide to sync with open prop
	onHide(event) {
		if (this.open) {
			this.open = false
			return false
		}

		return super.onHide(event)
	}

	// Handle Escape key
	handleEscape(event) {
		if (event.key === 'Escape' && this.open) {
			event.preventDefault()
			event.stopPropagation()
			this.open = false
		}
	}

	// Close this dropdown if another one opens
	handleOtherDropdownOpen(event) {
		if (event.detail.dropdown !== this.element && this.open) {
			this.open = false
		}
	}

	// Handle Tab key for focus trapping
	boundHandleTab(event) {
		if (event.key !== 'Tab' || !this.open) return

		const focusableElements = this.getFocusableElements()
		const trigger = this.buttonTargets[0]

		if (!trigger) return

		// If no focusable elements in menu, just cycle between trigger and itself
		if (!focusableElements.length) {
			event.preventDefault()
			return
		}

		const firstElement = focusableElements[0]
		const lastElement = focusableElements[focusableElements.length - 1]
		const currentElement = document.activeElement

		// Create focus cycle: first -> ... -> last -> trigger -> first

		// Shift+Tab on first element -> go to trigger
		if (event.shiftKey && currentElement === firstElement) {
			event.preventDefault()
			trigger.focus()
			return
		}

		// Tab on trigger -> go to first element
		if (!event.shiftKey && currentElement === trigger) {
			event.preventDefault()
			firstElement.focus()
			return
		}

		// Tab on last element -> go to trigger
		if (!event.shiftKey && currentElement === lastElement) {
			event.preventDefault()
			trigger.focus()
			return
		}

		// Shift+Tab on trigger -> go to last element
		if (event.shiftKey && currentElement === trigger) {
			event.preventDefault()
			lastElement.focus()
			return
		}

		// Check if focus is inside menu
		const isInsideMenu = this.menuTarget && this.menuTarget.contains(currentElement)
		const isTrigger = currentElement === trigger

		// If focus is outside dropdown (shouldn't happen, but safety check)
		if (!isInsideMenu && !isTrigger) {
			event.preventDefault()
			firstElement.focus()
		}
	}

	// Get all focusable elements in the dropdown menu
	getFocusableElements() {
		if (!this.menuTarget) return []

		const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
		return Array.from(this.menuTarget.querySelectorAll(selector))
	}

	// Focus the first focusable element in the dropdown
	focusFirstElement() {
		requestAnimationFrame(() => {
			const focusableElements = this.getFocusableElements()
			if (focusableElements.length > 0) {
				focusableElements[0].focus()
			} else if (this.menuTarget) {
				// Fallback: focus menu if no focusable elements
				this.menuTarget.setAttribute('tabindex', '-1')
				this.menuTarget.focus()
			}
		})
	}

	// Focus the trigger button
	focusTrigger() {
		const trigger = this.buttonTargets[0]
		if (trigger) {
			requestAnimationFrame(() => {
				trigger.focus()
			})
		}
	}
}
